"use client";

import { useState } from "react";
import Image from "next/image";
import { Container, BlueButton } from "./Primitives";

type CarClass = {
  key: string;
  label: string;
  model: string;
  image: string;
};

const CLASSES: CarClass[] = [
  { key: "comfort", label: "Comfort +", model: "Mercedes-Benz E-Class", image: "/img/cars/comfort.jpg" },
  { key: "business", label: "Business", model: "BMW 5 Series", image: "/img/cars/business.png" },
  { key: "vip", label: "VIP", model: "Mercedes-Maybach S-Class", image: "/img/cars/vip.png" },
  { key: "suv", label: "SUV VIP", model: "Mercedes-Benz GLS", image: "/img/cars/suv.jpg" },
  { key: "minivan", label: "Minivan", model: "Mercedes-Benz V-Class", image: "/img/cars/minivan.png" },
  { key: "minibus", label: "Minibus", model: "Mercedes-Benz Sprinter", image: "/img/cars/minibus.png" },
  { key: "bus", label: "BUS", model: "Executive Coach", image: "/img/cars/bus.webp" },
];

/**
 * ComfortLevel — табы классов + карусель.
 *
 * Desktop (lg+): 3-card carousel. Активная по центру + prev/next по бокам
 *   меньшие и полупрозрачные. Сама карусель ВНЕ Container — на полную
 *   ширину viewport, чтобы боковые машины «выезжали» из под краёв.
 *
 * Mobile (<lg): только активная картинка, без боковых.
 *
 * Карусель циклическая — last next = первый, first prev = последний.
 */
export default function ComfortLevel() {
  const [activeIndex, setActiveIndex] = useState(2); // VIP

  return (
    <section id="cars" className="pt-12 pb-16 sm:pt-16 sm:pb-20 lg:pt-[85px] lg:pb-[85px]">
      <Container>
        <h2 className="mb-8 text-center text-[28px] font-medium tracking-[-0.5px] text-text sm:text-[32px] lg:text-[38px] xl:tracking-[-1px] lg:mb-10">
          Уровень комфорта
        </h2>

        {/* Tabs row */}
        <div className="mb-10 flex flex-wrap items-center justify-center gap-2 sm:gap-3 lg:mb-12">
          {CLASSES.map((c, i) => {
            const isActive = i === activeIndex;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => setActiveIndex(i)}
                className={`rounded-[50px] border px-5 py-2.5 text-[13px] font-medium transition-colors duration-200 sm:text-sm ${
                  isActive
                    ? "border-black bg-black text-white"
                    : "border-black bg-transparent text-black hover:bg-black/5"
                }`}
              >
                {c.label}
              </button>
            );
          })}
        </div>
      </Container>

      {/* Full-width carousel (вне Container — на всю ширину viewport).
          translate-x-[N%] — это N% от ширины слайда (= viewport width).
          С scale-[0.5] боковые имеют scaled-width ~480px и helpfully выезжают
          из-под краёв при больших viewport. overflow-hidden на родителе
          гарантирует что |d|≥2 (translate-x-[200%]) не дают horizontal scroll. */}
      <div className="relative mb-6 h-[220px] w-full overflow-hidden sm:h-[280px] lg:mb-8 lg:h-[400px]">
        {CLASSES.map((c, i) => {
          const n = CLASSES.length;
          let d = i - activeIndex;
          if (d > n / 2) d -= n;
          if (d < -n / 2) d += n;

          // Mobile: только активная (d=0) видна, остальные opacity-0.
          // Desktop (lg+): классическая карусель — 3 видимых слайда.
          let cls = "opacity-0 scale-50 -translate-x-[200%]";
          if (d === 0) {
            cls = "opacity-100 scale-100 translate-x-0 z-20";
          } else if (d === -1) {
            cls = "opacity-0 lg:opacity-40 lg:scale-[0.55] lg:-translate-x-[35%] z-10";
          } else if (d === 1) {
            cls = "opacity-0 lg:opacity-40 lg:scale-[0.55] lg:translate-x-[35%] z-10";
          } else if (d < -1) {
            cls = "opacity-0 scale-50 -translate-x-[200%]";
          } else {
            cls = "opacity-0 scale-50 translate-x-[200%]";
          }

          return (
            <div
              key={c.key}
              className={`absolute inset-0 flex items-center justify-center transition-all duration-500 ease-out ${cls}`}
              aria-hidden={d !== 0}
            >
              <div className="relative h-full w-full max-w-[860px] aspect-[860/360]">
                <Image
                  src={c.image}
                  alt={c.model}
                  fill
                  className="object-contain"
                  sizes="(max-width: 1024px) 100vw, 860px"
                />
              </div>
            </div>
          );
        })}
      </div>

      <Container>
        {/* Active model name */}
        <p className="mb-7 text-center text-[18px] font-medium text-text transition-opacity duration-300 sm:text-[20px] lg:text-[24px]">
          {CLASSES[activeIndex].model}
        </p>

        {/* CTA */}
        <div className="flex justify-center">
          <BlueButton />
        </div>
      </Container>
    </section>
  );
}
